// =====================================================
// Investor Q Podcast Admin - ADMIN ONLY
// Add this to your frontend routes
// =====================================================

import React, { useState, useEffect } from 'react';

// API_BASE already includes /api, so don't add it again
const API_BASE = process.env.REACT_APP_API_URL || '';

// Get the base URL for audio (without /api)
const getAudioUrl = (audioUrl) => {
  if (!audioUrl) return null;
  // If it's already a full URL, use as-is
  if (audioUrl.startsWith('http')) return audioUrl;
  // If it's a relative URL starting with /api, construct full URL
  // Remove /api from API_BASE if it ends with it
  const baseUrl = API_BASE.replace(/\/api$/, '');
  return `${baseUrl}${audioUrl}`;
};

const PodcastAdmin = ({ user, setActiveTab }) => {
  const [episodes, setEpisodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [result, setResult] = useState(null);
  const [selectedScript, setSelectedScript] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [ttsConfig, setTtsConfig] = useState(null);

  // Helper to get auth headers
  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'x-user-id': user?.id?.toString() || ''
  });

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/podcast/config`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTtsConfig(data);
      }
    } catch (error) {
      console.log('Could not load TTS config');
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadData();
      loadConfig();
    }
  }, [user?.id]);

  // Show login prompt if no user
  if (!user?.id) {
    return (
      <div style={{ 
        minHeight: '50vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '1rem',
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <h2 style={{ color: '#f87171', margin: '0 0 1rem' }}>🔒 Admin Access Required</h2>
          <p style={{ color: '#94a3b8', margin: 0 }}>
            Please log in with an admin account to access the Podcast Content Engine.
          </p>
        </div>
      </div>
    );
  }

  const loadData = async () => {
    setLoading(true);
    setAuthError(null);
    try {
      const [episodesRes, categoriesRes, modulesRes] = await Promise.all([
        fetch(`${API_BASE}/podcast/episodes`, { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/podcast/categories`).then(r => r.json()), // Public
        fetch(`${API_BASE}/podcast/modules`).then(r => r.json())     // Public
      ]);
      
      // Check for auth error
      if (episodesRes.error === 'Admin access required') {
        setAuthError(episodesRes.message);
        setEpisodes([]);
      } else {
        setEpisodes(episodesRes.episodes || []);
      }
      setCategories(categoriesRes.categories || []);
      setModules(modulesRes.modules || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const generateEpisode = async () => {
    if (!selectedCategory) {
      alert('Please select a category');
      return;
    }
    
    setGenerating(true);
    setResult(null);
    setAuthError(null);
    
    try {
      const response = await fetch(`${API_BASE}/podcast/episodes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          category: selectedCategory,
          moduleId: selectedModule || undefined
        })
      });
      
      const data = await response.json();
      
      if (data.error === 'Admin access required') {
        setAuthError(data.message);
        setResult(null);
      } else {
        setResult(data);
        if (data.success) {
          loadData();
        }
      }
    } catch (error) {
      setResult({ success: false, error: error.message });
    }
    
    setGenerating(false);
  };

  const viewScript = async (episodeId) => {
    try {
      // Fetch both script and episode details (for audio URL)
      const [scriptRes, episodeRes] = await Promise.all([
        fetch(`${API_BASE}/podcast/episodes/${episodeId}/script?format=notebooklm`, {
          headers: getAuthHeaders()
        }),
        fetch(`${API_BASE}/podcast/episodes/${episodeId}`, {
          headers: getAuthHeaders()
        })
      ]);
      
      const scriptText = await scriptRes.text();
      const episodeData = await episodeRes.json();
      
      setSelectedScript({ 
        episodeId, 
        content: scriptText,
        audioUrl: episodeData.episode?.audio_url || null,
        title: episodeData.episode?.title || 'Episode'
      });
    } catch (error) {
      console.error('Error loading script:', error);
      alert('Error loading script');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const approveEpisode = async (episodeId) => {
    try {
      await fetch(`${API_BASE}/podcast/episodes/${episodeId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      loadData();
    } catch (error) {
      alert('Error approving episode');
    }
  };

  const publishEpisode = async (episodeId) => {
    if (!window.confirm('Publish this episode? It will be visible to all users in the Learning section and pushed to RSS.com (if configured).')) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/podcast/episodes/${episodeId}/publish`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok) {
        let message = `✅ Episode #${data.episodeNumber} published!`;
        if (data.rssPublished) {
          message += '\n📡 Successfully pushed to RSS.com';
        } else if (data.rssError) {
          message += `\n⚠️ RSS.com: ${data.rssError}`;
        } else {
          message += '\nℹ️ RSS.com not configured';
        }
        alert(message);
      } else {
        alert(`Error: ${data.error || 'Unknown error'}`);
      }
      loadData();
    } catch (error) {
      alert('Error publishing episode: ' + error.message);
    }
  };

  const deleteEpisode = async (episodeId) => {
    if (!window.confirm('Delete this episode permanently? This cannot be undone.')) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/podcast/episodes/${episodeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        alert('Episode deleted.');
      }
      loadData();
    } catch (error) {
      alert('Error deleting episode');
    }
  };

  const rejectEpisode = async (episodeId) => {
    const reason = window.prompt('Reason for rejection (optional):');
    try {
      await fetch(`${API_BASE}/podcast/episodes/${episodeId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason })
      });
      loadData();
    } catch (error) {
      alert('Error rejecting episode');
    }
  };

  const filteredModules = selectedCategory 
    ? modules.filter(m => m.category === selectedCategory)
    : [];

  const statusColors = {
    draft: '#94a3b8',
    script_ready: '#f59e0b',
    audio_ready: '#3b82f6',
    approved: '#22c55e',
    published: '#8b5cf6',
    linked: '#06b6d4',
    rejected: '#ef4444'
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)', 
      color: '#e2e8f0',
      padding: '1rem'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>🎙️ Podcast Content Engine</h1>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem' }}>Generate AI-powered educational podcasts for InvestorQ Learning Academy</p>
          <p style={{ margin: '0.5rem 0 0', color: '#60a5fa', fontSize: '0.75rem' }}>
            🔒 Admin: {user?.email}
          </p>
          
          {/* TTS Config Status */}
          {ttsConfig && (
            <div style={{ 
              marginTop: '0.75rem', 
              padding: '0.5rem 0.75rem', 
              background: 'rgba(30, 41, 59, 0.6)',
              borderRadius: '0.5rem',
              fontSize: '0.75rem',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem'
            }}>
              <span>
                <strong>TTS:</strong>{' '}
                {ttsConfig.tts?.primary === 'elevenlabs' && <span style={{ color: '#22c55e' }}>🎯 ElevenLabs</span>}
                {ttsConfig.tts?.primary === 'openai' && <span style={{ color: '#22c55e' }}>🎯 OpenAI</span>}
                {ttsConfig.tts?.primary === 'replicate' && <span style={{ color: '#f59e0b' }}>🔄 Replicate</span>}
                {ttsConfig.tts?.primary === 'none' && <span style={{ color: '#ef4444' }}>❌ None</span>}
              </span>
              <span>
                <strong>Storage:</strong>{' '}
                {ttsConfig.storage?.r2 ? <span style={{ color: '#22c55e' }}>☁️ R2</span> : <span style={{ color: '#f59e0b' }}>💾 Cache</span>}
              </span>
              <span>
                <strong>RSS:</strong>{' '}
                {ttsConfig.rss?.configured ? (
                  <span style={{ color: '#22c55e' }}>✓ {ttsConfig.rss?.showId || 'Configured'}</span>
                ) : (
                  <span style={{ color: '#94a3b8' }} title="Set RSS_API_KEY and RSS_SHOW_ID in Railway">
                    — Not configured
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Video Tools */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveTab && setActiveTab('video-admin')}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600'
              }}
            >
              🎬 Podcast Videos
            </button>
            
            <button
              onClick={() => setActiveTab && setActiveTab('marketing-videos')}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600'
              }}
            >
              📸 Marketing Videos
            </button>
          </div>

        </div>



        {/* Auth Error */}
        {authError && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.75rem',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ margin: 0, color: '#f87171' }}>
              ⚠️ <strong>Access Denied:</strong> {authError}
            </p>
          </div>
        )}

        {/* Generate New Episode */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '1rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <h2 style={{ margin: '0 0 1rem', fontSize: '1.125rem' }}>✨ Generate New Episode</h2>
          
          {/* Mobile-friendly stacked layout */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#94a3b8' }}>Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedModule('');
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '0.5rem',
                  color: '#e2e8f0',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select category...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#94a3b8' }}>Module (Optional)</label>
              <select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                disabled={!selectedCategory}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '0.5rem',
                  color: '#e2e8f0',
                  fontSize: '1rem',
                  opacity: selectedCategory ? 1 : 0.5
                }}
              >
                <option value="">General category episode...</option>
                {filteredModules.map(mod => (
                  <option key={mod.id} value={mod.id}>{mod.name}</option>
                ))}
              </select>
            </div>
            
            <button
              onClick={generateEpisode}
              disabled={generating || !selectedCategory}
              style={{
                width: '100%',
                padding: '1rem 1.5rem',
                background: generating ? 'rgba(99, 102, 241, 0.3)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontWeight: '600',
                cursor: generating ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontSize: '1rem'
              }}
            >
              {generating ? '⏳ Generating Script & Audio...' : '🚀 Generate Episode'}
            </button>
          </div>

          {/* Result */}
          {result && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              borderRadius: '0.5rem',
              background: result.success ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${result.success ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
            }}>
              {result.success ? (
                <div>
                  <strong style={{ color: '#22c55e' }}>✅ Episode Created!</strong>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
                    <strong>Title:</strong> {result.script?.title}<br/>
                    <strong>ID:</strong> {result.episodeId}<br/>
                    <strong>Status:</strong> {result.status === 'audio_ready' ? '🎤 Audio Ready' : '📝 Script Ready'}
                    {result.audioProvider && (
                      <><br/><strong>TTS Provider:</strong> {
                        result.audioProvider === 'elevenlabs' ? '🎯 ElevenLabs' :
                        result.audioProvider === 'openai-tts' ? '🎯 OpenAI TTS (onyx)' :
                        result.audioProvider === 'replicate-xtts' ? '🔄 Replicate XTTS' :
                        result.audioProvider
                      }</>
                    )}
                  </p>
                  
                  {/* Audio Player */}
                  {result.audioUrl && (
                    <div style={{ marginTop: '1rem' }}>
                      <strong style={{ color: '#60a5fa' }}>🎧 Generated Audio:</strong>
                      <audio 
                        controls 
                        style={{ width: '100%', marginTop: '0.5rem' }}
                        src={getAudioUrl(result.audioUrl)}
                      >
                        Your browser does not support the audio element.
                      </audio>
                      <a 
                        href={getAudioUrl(result.audioUrl)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'inline-block',
                          marginTop: '0.5rem',
                          color: '#60a5fa',
                          fontSize: '0.8rem'
                        }}
                      >
                        📥 Download Audio
                      </a>
                    </div>
                  )}
                  
                  {/* Next Step */}
                  <p style={{ margin: '0.75rem 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>
                    {result.nextStep}
                  </p>
                </div>
              ) : (
                <div style={{ color: '#ef4444' }}>
                  <strong>❌ Error:</strong> {result.error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Episodes List */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '1rem',
          padding: '1rem',
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.125rem' }}>📻 Episodes ({episodes.length})</h2>
            <button
              onClick={loadData}
              disabled={loading}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(99, 102, 241, 0.2)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                borderRadius: '0.5rem',
                color: '#a5b4fc',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              🔄 Refresh
            </button>
          </div>

          {loading ? (
            <p style={{ textAlign: 'center', color: '#94a3b8' }}>Loading...</p>
          ) : episodes.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#94a3b8' }}>No episodes yet. Generate your first one above!</p>
          ) : (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {episodes.map(episode => (
                <div
                  key={episode.episode_id}
                  style={{
                    background: 'rgba(15, 23, 42, 0.5)',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        background: `${statusColors[episode.status] || '#94a3b8'}20`,
                        color: statusColors[episode.status] || '#94a3b8'
                      }}>
                        {episode.status?.replace('_', ' ')}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{episode.episode_id}</span>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {new Date(episode.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>{episode.title || 'Untitled'}</h3>
                  
                  <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.75rem' }}>
                    <span>📁 {episode.category}</span>
                    {episode.module_id && <span>📚 {episode.module_id}</span>}
                    {episode.audio_duration_seconds > 0 && (
                      <span>⏱️ {Math.floor(episode.audio_duration_seconds / 60)}:{(episode.audio_duration_seconds % 60).toString().padStart(2, '0')}</span>
                    )}
                  </div>
                  
                  {/* Workflow Buttons based on Status */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {/* View Script - always available */}
                    <button
                      onClick={() => viewScript(episode.episode_id)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '0.375rem',
                        color: '#3b82f6',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      📄 Script
                    </button>
                    
                    {/* Approve - for script_ready or audio_ready */}
                    {(episode.status === 'script_ready' || episode.status === 'audio_ready') && (
                      <>
                        <button
                          onClick={() => approveEpisode(episode.episode_id)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: 'rgba(34, 197, 94, 0.2)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            borderRadius: '0.375rem',
                            color: '#22c55e',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          ✅ Approve
                        </button>
                        <button
                          onClick={() => rejectEpisode(episode.episode_id)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '0.375rem',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          ❌ Reject
                        </button>
                      </>
                    )}
                    
                    {/* Publish - for approved episodes */}
                    {episode.status === 'approved' && (
                      <button
                        onClick={() => publishEpisode(episode.episode_id)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(99, 102, 241, 0.3))',
                          border: '1px solid rgba(139, 92, 246, 0.5)',
                          borderRadius: '0.375rem',
                          color: '#a78bfa',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}
                      >
                        🚀 Publish
                      </button>
                    )}
                    
                    {/* Published indicator */}
                    {episode.status === 'published' && (
                      <span style={{
                        padding: '0.5rem 0.75rem',
                        background: 'rgba(34, 197, 94, 0.1)',
                        border: '1px solid rgba(34, 197, 94, 0.3)',
                        borderRadius: '0.375rem',
                        color: '#22c55e',
                        fontSize: '0.8rem'
                      }}>
                        ✅ Live {episode.rss_published ? '+ 📡 RSS' : ''}
                      </span>
                    )}
                    
                    {/* Delete - always available */}
                    <button
                      onClick={() => deleteEpisode(episode.episode_id)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: 'rgba(107, 114, 128, 0.2)',
                        border: '1px solid rgba(107, 114, 128, 0.3)',
                        borderRadius: '0.375rem',
                        color: '#6b7280',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                  
                  {/* Audio Player for episodes with audio */}
                  {episode.audio_url && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <audio 
                        controls 
                        style={{ width: '100%', height: '40px' }}
                        src={getAudioUrl(episode.audio_url)}
                      >
                        Your browser does not support audio.
                      </audio>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Script Modal */}
        {selectedScript && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.95)',
            zIndex: 1000,
            overflow: 'auto',
            padding: '1rem'
          }}>
            <div style={{
              maxWidth: '800px',
              margin: '0 auto',
              background: '#1e293b',
              borderRadius: '1rem',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '1rem',
                background: 'rgba(99,102,241,0.1)',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>
                  {selectedScript.audioUrl ? '🎧 Episode Audio & Script' : '📄 Script for NotebookLM'}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => copyToClipboard(selectedScript.content)}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: 'rgba(34, 197, 94, 0.2)',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      borderRadius: '0.5rem',
                      color: '#22c55e',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    📋 Copy Script
                  </button>
                  <button
                    onClick={() => setSelectedScript(null)}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.1)',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    ✕ Close
                  </button>
                </div>
              </div>
              
              <div style={{ padding: '1rem' }}>
                {/* Audio Player - show if audio exists */}
                {selectedScript.audioUrl && (
                  <div style={{
                    marginBottom: '1rem',
                    padding: '1rem',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '0.75rem'
                  }}>
                    <p style={{ margin: '0 0 0.75rem', color: '#22c55e', fontWeight: '600' }}>
                      🎤 Generated Audio Ready!
                    </p>
                    <audio 
                      controls 
                      style={{ width: '100%' }}
                      src={getAudioUrl(selectedScript.audioUrl)}
                    >
                      Your browser does not support audio.
                    </audio>
                    <a 
                      href={getAudioUrl(selectedScript.audioUrl)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        display: 'inline-block',
                        marginTop: '0.75rem',
                        padding: '0.5rem 1rem',
                        background: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '0.5rem',
                        color: '#60a5fa',
                        textDecoration: 'none',
                        fontSize: '0.875rem'
                      }}
                    >
                      📥 Download Audio
                    </a>
                  </div>
                )}
                
                {/* Script content */}
                <pre style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  lineHeight: '1.5',
                  color: '#cbd5e1',
                  background: 'rgba(15,23,42,0.5)',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  maxHeight: selectedScript.audioUrl ? '30vh' : '50vh',
                  overflow: 'auto'
                }}>
                  {selectedScript.content}
                </pre>
                
                {/* Only show NotebookLM instructions if NO audio */}
                {!selectedScript.audioUrl && (
                  <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#94a3b8' }}>
                    <strong>Next Steps (Manual Audio):</strong><br/>
                    1. Copy this script<br/>
                    2. Go to <a href="https://notebooklm.google.com" target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1' }}>notebooklm.google.com</a><br/>
                    3. Create a new notebook and paste as a source<br/>
                    4. Click "Generate Audio Overview"<br/>
                    5. Download the generated podcast audio
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PodcastAdmin;